import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, EmptyState, Modal, Alert, Label, Spinner } from "@/components/ui";
import { fileToBase64, todayISO } from "@/lib/helpers";
import { Plus, Pill, Check, Camera } from "lucide-react";
import { toast } from "sonner";

export default function Meds() {
  const [meds, setMeds] = useState(null);
  const [logs, setLogs] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    const [m, l] = await Promise.all([api.listMeds(), api.getMedLogs(todayISO())]);
    setMeds(m); setLogs(l);
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = async (med_id, scheduled_time, status) => {
    await api.logMed({ med_id, scheduled_time, status, date: todayISO() });
    await load();
  };

  if (!meds) return <Spinner />;

  const doses = meds.flatMap((m) => (m.schedule_times || []).map((t) => ({ med: m, time: t })))
    .sort((a, b) => a.time.localeCompare(b.time));
  const groups = [
    { title: "Morning", items: doses.filter((d) => d.time < "12:00") },
    { title: "Afternoon", items: doses.filter((d) => d.time >= "12:00" && d.time < "17:00") },
    { title: "Evening", items: doses.filter((d) => d.time >= "17:00") },
  ];

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="Medications" title="Today's schedule"
        action={<Button variant="accent" data-testid="add-med-button" onClick={() => setAddOpen(true)}><Plus className="w-4 h-4" /> Add</Button>} />

      {doses.length === 0 && (
        <Card><EmptyState testid="meds-empty" icon={Pill} title="No medications yet"
          body="Scan a label or add one manually — we'll track every scheduled dose."
          action={<Button data-testid="meds-empty-add-cta" onClick={() => setAddOpen(true)}>Add a medication</Button>} /></Card>
      )}

      {groups.map((g) => g.items.length > 0 && (
        <div key={g.title} className="mt-6">
          <Label>{g.title}</Label>
          <Card className="p-0 overflow-hidden mt-2">
            {g.items.map(({ med, time }, i) => {
              const done = logs.find((l) => l.med_id === med.id && l.scheduled_time === time && l.date === todayISO() && l.status === "taken");
              const skipped = logs.find((l) => l.med_id === med.id && l.scheduled_time === time && l.date === todayISO() && l.status === "skipped");
              return (
                <div key={med.id + time} className={`flex items-center gap-3 p-4 ${i > 0 ? "border-t border-line" : ""}`}>
                  <div className="flex-1">
                    <div className="font-semibold text-ink">{med.name}</div>
                    <div className="text-sm text-subtle">{med.dosage} · {time}{med.notes ? ` · ${med.notes}` : ""}</div>
                  </div>
                  <button data-testid={`med-taken-${med.id}-${time}`} onClick={() => mark(med.id, time, "taken")}
                    className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full ${done ? "bg-ok text-white" : "bg-brand-tint text-brand-dark border border-line hover:bg-white"}`}>
                    <Check className="w-3.5 h-3.5" /> Taken
                  </button>
                  <button data-testid={`med-skip-${med.id}-${time}`} onClick={() => mark(med.id, time, "skipped")}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full ${skipped ? "bg-warn text-white" : "bg-brand-tint text-brand-dark border border-line hover:bg-white"}`}>
                    Skip
                  </button>
                </div>
              );
            })}
          </Card>
        </div>
      ))}

      <AddMedModal open={addOpen} onClose={() => setAddOpen(false)} onSaved={async () => { setAddOpen(false); await load(); toast.success("Medication added"); }} />
    </div>
  );
}

function AddMedModal({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [dosage, setDosage] = useState("");
  const [times, setTimes] = useState("08:00");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null); setInfo(null); setScanning(true);
    try {
      const file_base64 = await fileToBase64(file);
      const mime_type = file.type === "image/png" ? "image/png" : "image/jpeg";
      const data = await api.scanMed({ file_base64, mime_type });
      if (data.name) setName(data.name);
      if (data.dosage) setDosage(data.dosage);
      if (data.schedule_times?.length) setTimes(data.schedule_times.join(", "));
      if (data.notes) setNotes(data.notes);
      const filled = [data.name && "name", data.dosage && "dosage", data.schedule_times?.length && "schedule"].filter(Boolean);
      setInfo(filled.length ? `Scanned ${filled.join(", ")}. Review and save.` : "Scan complete — please review the fields.");
    } catch (ex) {
      setErr(ex?.message || "Could not read that label");
    } finally { setScanning(false); }
  };

  const save = async () => {
    setErr(null); setBusy(true);
    try {
      const schedule = times.split(",").map((t) => t.trim()).filter(Boolean);
      await api.createMed({ name: name.trim(), dosage: dosage.trim(), schedule_times: schedule, notes: notes.trim() || null });
      setName(""); setDosage(""); setTimes("08:00"); setNotes(""); setInfo(null);
      onSaved();
    } catch (ex) {
      setErr(ex?.message || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add medication" testid="add-med-modal">
      <Label>Scan the label (optional)</Label>
      <label className="mt-2 mb-4 flex items-center gap-3 border border-line rounded-lg p-4 bg-brand-tint cursor-pointer hover:bg-white transition-colors" data-testid="scan-med-file">
        <Camera className="w-5 h-5 text-brand-dark" />
        <div className="flex-1">
          <div className="font-semibold text-sm text-ink">Upload a photo of the label</div>
          <div className="text-xs text-subtle">{scanning ? "Reading the label…" : "PNG or JPG — AI extracts the fields"}</div>
        </div>
        <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={scanning} />
      </label>
      <Alert tone="success">{info}</Alert>

      <Field label="Name" data-testid="med-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Metformin" />
      <Field label="Dosage" data-testid="med-dosage-input" value={dosage} onChange={(e) => setDosage(e.target.value)} placeholder="e.g., 500 mg" />
      <Field label="Times (comma-separated, 24h)" data-testid="med-times-input" value={times} onChange={(e) => setTimes(e.target.value)} placeholder="08:00, 20:00" />
      <Field label="Notes (optional)" data-testid="med-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="With food" />
      <Alert testid="add-med-error">{err}</Alert>
      <Button data-testid="save-med-button" onClick={save} loading={busy}
        disabled={!name.trim() || !dosage.trim() || !times.trim()} className="w-full mt-2">Save medication</Button>
    </Modal>
  );
}
